import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import LoadingSpinner from "../../HelperComponents/LoadingSpinner";
import {
  getApplicant,
  getSeniorDirector,
  getUserProfileForCurrentUser,
  getUsersFullName,
} from "../../Service/Services";
import {
  getAllLeadershipUsernames,
  getFieldDisplayNames,
} from "../../HelperComponents/Form/Utility";

type EmployeeSummaryProps = {
  user: any;
  staff: any;
  pbsRegionsServiced: any;
};

const EmployeeSummary = (props: EmployeeSummaryProps) => {
  const [loading, setLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<any>();
  const [applicant, setApplicant] = useState<any>();
  const [fullNamesMap, setFullNamesMap] = useState<Record<string, string>>({});
  const [seniorDirector, setSeniorDirector] = useState<any>();

  // Fetch full names for all leadership users
  const fetchLeadershipFullNames = useCallback(async () => {
    try {
      const fields = [
        props.pbsRegionsServiced?.RegionalCoordinator,
        props.pbsRegionsServiced?.RegionalAssistant,
        props.pbsRegionsServiced?.RegionalHR,
        props.pbsRegionsServiced?.RegionalClinicalTrainingCoordinator,
        props.pbsRegionsServiced?.SeniorAnalyst,
        props.pbsRegionsServiced?.RegionalDirector,
        props.pbsRegionsServiced?.ClinicalDirector,
      ];

      const loginNames = getAllLeadershipUsernames(fields);
      if (loginNames.length === 0) return;

      const response = await getUsersFullName(loginNames);
      const namesMap: Record<string, string> = {};

      response.data.profiles?.forEach((profile: any) => {
        if (profile.accountName && profile.dfcFullName) {
          namesMap[profile.accountName.toUpperCase()] = profile.dfcFullName;
        }
      });

      setFullNamesMap(namesMap);
    } catch (error) {
      console.error("Error fetching full names:", error);
    }
  }, [props.pbsRegionsServiced]);

  const fetchApplicant = useCallback(async () => {
    try {
      setLoading(true);
      const loginNameWithoutDomain = props.user?.LoginName.replace(
        "POSITIVE\\",
        "",
      );
      const response = await getApplicant(loginNameWithoutDomain);
      setApplicant(response.data.items[0]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [props.user?.LoginName]);

  const fetchUserProfileProperties = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getUserProfileForCurrentUser(
        props.user?.LoginName,
      );
      setUserProfile(response.data.properties);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [props.user?.LoginName]);

  const fetchSeniorDirector = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getSeniorDirector(props.user?.Region);
      setSeniorDirector(response.data.items[0]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [props.user?.Region]);

  useEffect(() => {
    if (!props.user) {
      setLoading(true);
      return;
    }
    fetchUserProfileProperties();
    fetchApplicant();
    fetchLeadershipFullNames();
    fetchSeniorDirector();
  }, [
    props.user,
    fetchUserProfileProperties,
    fetchApplicant,
    fetchLeadershipFullNames,
    fetchSeniorDirector,
  ]);

  return (
    <>
      {loading && <LoadingSpinner />}
      {!loading && (
        <div className="editProfile-container">
          <div className="grid">
            {/* Personal Information Card */}
            <div className="card" style={{ gridRowEnd: "span 34" }}>
              <h3 className="sectionTitle">Personal Information</h3>
              <div className="detailRow">
                <span className="label">Applicant ID</span>
                <span className="value">{applicant?.ID}</span>
              </div>
              <div className="detailRow">
                <span className="label">First Name</span>
                <span className="value">{userProfile?.FirstName}</span>
              </div>
              <div className="detailRow">
                <span className="label">Last Name</span>
                <span className="value">{userProfile?.LastName}</span>
              </div>
              <div className="detailRow">
                <span className="label">Middle Name</span>
                <span className="value">{userProfile?.MiddleName}</span>
              </div>
              <div className="detailRow">
                <span className="label">legal Name</span>
                <span className="value">
                  {userProfile?.CurrentTaxReportingName}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Preferred Name</span>
                <span className="value">{userProfile?.PreferredName}</span>
              </div>
              <div className="detailRow">
                <span className="label">Username</span>
                <span className="value">{userProfile?.UserName}</span>
              </div>
              <div className="detailRow">
                <span className="label">Date of Birth</span>
                <span className="value">
                  {userProfile?.BirthDate &&
                    moment(userProfile?.BirthDate).format("MM/DD/YYYY")}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">City of Birth</span>
                <span className="value">{userProfile?.CityOfBirth}</span>
              </div>
              <div className="detailRow">
                <span className="label">State of Birth</span>
                <span className="value">{userProfile?.Stateofbirth}</span>
              </div>
              <div className="detailRow">
                <span className="label">Gender</span>
                <span className="value">{userProfile?.Gender}</span>
              </div>
              <div className="detailRow">
                <span className="label">PBS Live Extension</span>
                <span className="value">{props.user?.PBSLiveExtension}</span>
              </div>
              <div className="detailRow">
                <span className="label">Home Phone</span>
                <span className="value">
                  <a
                    style={{ color: "inherit" }}
                    href={"tel:" + userProfile?.HomePhone}
                  >
                    {userProfile?.HomePhone}
                  </a>
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Work Phone</span>
                <span className="value">
                  <a
                    style={{ color: "inherit" }}
                    href={"tel:" + userProfile?.WorkPhone}
                  >
                    {userProfile?.WorkPhone}
                  </a>
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Mobile Phone</span>
                <span className="value">
                  <a
                    style={{ color: "inherit" }}
                    href={"tel:" + userProfile?.CellPhone}
                  >
                    {userProfile?.CellPhone}
                  </a>
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Email</span>
                <span className="value">
                  <a
                    style={{ color: "inherit" }}
                    href={"mailto:" + userProfile?.WorkEmail}
                  >
                    {userProfile?.WorkEmail}
                  </a>
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Personal Email</span>
                <span className="value">
                  <a
                    style={{ color: "inherit" }}
                    href={"mailto:" + userProfile?.PersonalEmail}
                  >
                    {userProfile?.PersonalEmail}
                  </a>
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Home Address</span>
                <span className="value">{userProfile?.Streetaddress}</span>
              </div>
              <div className="detailRow">
                <span className="label">Home Address Line 2</span>
                <span className="value">{userProfile?.Streetaddressline2}</span>
              </div>
              <div className="detailRow">
                <span className="label">City</span>
                <span className="value">{userProfile?.City}</span>
              </div>
              <div className="detailRow">
                <span className="label">State</span>
                <span className="value">{userProfile?.State}</span>
              </div>
              <div className="detailRow">
                <span className="label">Zip</span>
                <span className="value">{userProfile?.Zip}</span>
              </div>
              <div className="detailRow">
                <span className="label">County Of Residence</span>
                <span className="value">{userProfile?.CountyofResidence}</span>
              </div>
              {userProfile?.SameMailingAndHomeAddress === "true" ? (
                <div className="detailRow">
                  <span className="label">Mailing Address</span>
                  <span className="value">Same as Residential Address</span>
                </div>
              ) : (
                <>
                  <div className="detailRow">
                    <span className="label">Mailing Address</span>
                    <span className="value">
                      {userProfile?.MailingStreetAddress}
                    </span>
                  </div>
                  <div className="detailRow">
                    <span className="label">Mailing Address Line 2</span>
                    <span className="value">
                      {userProfile?.Mailingstreetaddressline2}
                    </span>
                  </div>
                  <div className="detailRow">
                    <span className="label">Mailing City</span>
                    <span className="value">{userProfile?.MailingCity}</span>
                  </div>
                  <div className="detailRow">
                    <span className="label">Mailing State</span>
                    <span className="value">{userProfile?.MailingState}</span>
                  </div>
                  <div className="detailRow">
                    <span className="label">Mailing Zip</span>
                    <span className="value">{userProfile?.MailingZip}</span>
                  </div>
                </>
              )}
              <div className="detailRow">
                <span className="label">Languages Spoken</span>
                <span className="value">
                  {userProfile?.Languagesspoken.replaceAll("|", ", ")}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Highest Degree</span>
                <span className="value">{userProfile?.HighestDegreeType}</span>
              </div>
            </div>
            {/* Status Card */}
            <div className="card" style={{ gridRowEnd: "span 7" }}>
              <h3 className="sectionTitle">Status</h3>

              <div className="detailRow">
                <span className="label">Status</span>
                <span
                  className={`value ${
                    props.user?.Active ? "active" : "inactive"
                  }`}
                >
                  {props.user?.Active ? "Active" : "Disabled"}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Disabling Date</span>
                <span className="value">
                  {props.staff?.Disabling_x0020_Date}
                </span>
              </div>
            </div>

            {/* Employment Details Card */}
            <div className="card" style={{ gridRowEnd: "span 16" }}>
              <h3 className="sectionTitle">Employment Details</h3>

              <div className="detailRow">
                <span className="label">Job Title</span>
                <span className="value">{userProfile?.["SPS-JobTitle"]}</span>
              </div>
              <div className="detailRow">
                <span className="label">Analyst Level</span>
                <span className="value">{userProfile?.AnalystLevel}</span>
              </div>
              <div className="detailRow">
                <span className="label">Employee Status</span>
                <span className="value">
                  {userProfile?.EmployeeStatus || props.user?.EmpStatus}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Region</span>
                <span className="value">
                  {userProfile?.Region || props.user?.Region}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Department</span>
                <span className="value">{props.user?.Department}</span>
              </div>
              <div className="detailRow">
                <span className="label">Hire Date</span>
                <span className="value">
                  {userProfile?.HireDate &&
                    moment(userProfile?.HireDate).format("MM/DD/YYYY")}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Start Date</span>
                <span className="value">
                  {applicant?.Employee_x0020_Start_x0020_Date &&
                    moment(applicant?.Employee_x0020_Start_x0020_Date).format(
                      "MM/DD/YYYY",
                    )}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Do Not Email</span>
                <span className="value">
                  {props.user?.DontEmail ? "Yes" : "No"}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Do Not Text</span>
                <span className="value">
                  {props.user?.DontText ? "Yes" : "No"}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Effective Region</span>
                <span className="value">{userProfile?.EffectiveRegion}</span>
              </div>
              <div className="detailRow">
                <span className="label">Effective Role</span>
                <span className="value">{userProfile?.EffectiveRole}</span>
              </div>
            </div>

            {/* Emergency Contact Card */}
            <div className="card" style={{ gridRowEnd: "span 7" }}>
              <h3 className="sectionTitle">Emergency Contact</h3>
              <div className="detailRow">
                <span className="label">Contact Name</span>
                <span className="value">
                  {userProfile?.EmergencyContactName}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Contact Number</span>
                <span className="value">
                  <a
                    style={{ color: "inherit" }}
                    href={"tel:" + userProfile?.EmergencyContactNumber}
                  >
                    {userProfile?.EmergencyContactNumber}
                  </a>
                </span>
              </div>
            </div>

            {/* Leadership Card */}
            <div className="card" style={{ gridRowEnd: "span 19" }}>
              <h3 className="sectionTitle">Leadership</h3>

              <div className="detailRow">
                <span className="label">Regional Coordinator(s)</span>
                <span className="value">
                  {getFieldDisplayNames(
                    props.pbsRegionsServiced?.RegionalCoordinator,
                    fullNamesMap,
                  )}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Client Services Specialist(s)</span>
                <span className="value">
                  {getFieldDisplayNames(
                    props.pbsRegionsServiced?.RegionalAssistant,
                    fullNamesMap,
                  )}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Staffing Services Specialist(s)</span>
                <span className="value">
                  {getFieldDisplayNames(
                    props.pbsRegionsServiced?.RegionalHR,
                    fullNamesMap,
                  )}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">
                  Regional Clinical Training Coordinator(s)
                </span>
                <span className="value">
                  {getFieldDisplayNames(
                    props.pbsRegionsServiced
                      ?.RegionalClinicalTrainingCoordinator,
                    fullNamesMap,
                  )}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Senior Analyst(s)</span>
                <span className="value">
                  {getFieldDisplayNames(
                    props.pbsRegionsServiced?.SeniorAnalyst,
                    fullNamesMap,
                  )}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Regional Director(s)</span>
                <span className="value">
                  {getFieldDisplayNames(
                    props.pbsRegionsServiced?.RegionalDirector,
                    fullNamesMap,
                  )}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Clinical Director(s)</span>
                <span className="value">
                  {getFieldDisplayNames(
                    props.pbsRegionsServiced?.ClinicalDirector,
                    fullNamesMap,
                  )}
                </span>
              </div>
              <div className="detailRow">
                <span className="label">Senior Clinical Director</span>
                <span className="value">
                  {seniorDirector?.SeniorDirector?.LookupValue}
                </span>
              </div>
            </div>

            {/* About Me Card */}
            <div className="card">
              <h3 className="sectionTitle">About Me</h3>
              <span className="value">{userProfile?.AboutMe}</span>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmployeeSummary;
